import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  RightOutlined,
  UnorderedListOutlined
} from '@ant-design/icons'
import { DatesSetArg, EventContentArg } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction'
import FullCalendar from '@fullcalendar/react'
import { Badge, Button, Modal, Spin, Tooltip } from 'antd'
import dayjs from 'dayjs'
import useDirectory from 'hooks/useDirectory'
import { AlertCalendarEvent } from 'types/alert'
import { AnnotationType } from 'types/building'
import CommonFunction from 'utils/commonFunction'

import useAlertCalendar from '../../hooks/use-alert-calendar'
import FolderIcon from '../building/buildingFileList/components/FolderIcon'

import AlertListView from './AlertListView'

import './AlertCalendar.css'

// ---- Popup showing alerts when a calendar day is clicked ----
function DayEventsPopup({
  date,
  events,
  onClose
}: {
  date: Date | null
  events: AlertCalendarEvent[]
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { getBuiildingType } = useDirectory()

  if (!date) return null

  const getAnnotationLabel = (type: number) => {
    switch (type) {
      case AnnotationType.FIRE:
        return '火災'
      case AnnotationType.FLOOD:
        return '水害'
      case AnnotationType.EARTHQUAKE:
        return '地震'
      case AnnotationType.VOLCANO:
        return '降灰'
      case AnnotationType.LIFE_LINE:
        return 'ライフライン'
      case AnnotationType.MEMO:
        return 'メモ'
      case AnnotationType.ARCHITECTURE:
        return '建築'
      case AnnotationType.AIR_CONDITION:
        return '空調衛生'
      case AnnotationType.ELECTRICITY:
        return '電気'
      case AnnotationType.EQUIPMENT:
        return '機材'
      case AnnotationType.IMAGE:
        return 'ピクチャ'
      case AnnotationType.MOVIE:
        return 'ムービー'
      case AnnotationType.INFO:
        return 'インフォ'
      default:
        return ''
    }
  }

  const dateLabel = `${dayjs(date).year()}年 ${
    dayjs(date).month() + 1
  }月${dayjs(date).date()}日`
  const dayEvents = events
    .filter((ev) => dayjs(ev.start).isSame(dayjs(date), 'day'))
    .sort((a, b) => (a.start > b.start ? 1 : -1))

  return (
    <Modal
      open
      centered
      onCancel={onClose}
      title={
        <div style={{ textAlign: 'center', width: '100%' }}>{dateLabel}</div>
      }
      footer={
        <div className="flex justify-center">
          <Button
            type="primary"
            onClick={onClose}
            className="w-[120px] bg-[#3163b7] hover:!bg-[#2a559e] border-none"
          >
            閉じる
          </Button>
        </div>
      }
      width={650}
    >
      {dayEvents.length === 0 ? (
        <div className="alert-day-popup-empty">
          この日のアラートはありません
        </div>
      ) : (
        <div className="alert-day-popup-list">
          {dayEvents.map((ev) => {
            const startTime = dayjs(ev.start).format('H:mm')
            const endTime = ev.end ? dayjs(ev.end).format('H:mm') : null
            const timeRange = endTime ? `${startTime}〜${endTime}` : startTime
            const folderId = ev.extendedProps?.folderId as number | undefined
            const childrenId = ev.extendedProps?.childrenId as
              | number
              | undefined
            const annotationType = ev.extendedProps?.annotationType as
              | number
              | undefined
            let label = ''

            if (childrenId) {
              label = CommonFunction.getBreadcrumbTextByChidType(
                String(childrenId),
                t,
                getBuiildingType()
              )
            } else if (folderId) {
              label = CommonFunction.getBreadcrumbTextByFolderType(
                String(folderId),
                t
              )
            }

            return (
              <div key={ev.id} className="alert-day-popup-item">
                <span className="alert-day-popup-time">{timeRange}</span>
                {annotationType && (
                  <span className="alert-day-popup-type">
                    <FolderIcon
                      type={annotationType as AnnotationType}
                      size="small"
                    />
                    <span className="whitespace-nowrap">
                      {getAnnotationLabel(annotationType)}
                    </span>
                  </span>
                )}
                {label && (
                  <span className="alert-day-popup-label">{label}</span>
                )}
                <span className="alert-day-popup-title">{ev.title}</span>
              </div>
            )
          })}
        </div>
      )}
    </Modal>
  )
}

// Custom event renderer — defined OUTSIDE the parent component to satisfy react/no-unstable-nested-components
function CalendarEventContent({
  eventInfo,
  label,
  annotationType
}: {
  eventInfo: EventContentArg
  label: string
  annotationType?: number
}) {
  const { event } = eventInfo

  return (
    <Tooltip
      title={
        <span>
          <strong>{event.title}</strong>
          <br />
          {dayjs(event.start).format('HH:mm')}
        </span>
      }
      placement="top"
    >
      <div className="alert-calendar-event-pill">
        <span className="alert-calendar-event-time">
          {dayjs(event.start).format('HH:mm')}
        </span>
        {annotationType && (
          <span className="alert-calendar-event-icon" aria-hidden="true">
            <FolderIcon type={annotationType as AnnotationType} size="small" />
          </span>
        )}
        {label && <span className="alert-calendar-event-label">{label}</span>}
        <span className="alert-calendar-event-title">{event.title}</span>
      </div>
    </Tooltip>
  )
}

CalendarEventContent.defaultProps = {
  annotationType: undefined
}

// Custom day-cell renderer: renders only the numeric day (strips Japanese "日" appended by locale="ja")
// Defined OUTSIDE the parent component to satisfy react/no-unstable-nested-components
function DayCellNumber({ date }: { date: Date }) {
  return <span className="fc-daygrid-day-number-inner">{date.getDate()}</span>
}

type CalendarViewType = 'dayGridMonth' | 'dayGridWeek' | 'dayGridDay'
type PageViewMode = 'calendar' | 'list'

const CALENDAR_VIEW_LABELS: { key: CalendarViewType; label: string }[] = [
  { key: 'dayGridMonth', label: '月' },
  { key: 'dayGridWeek', label: '週' },
  { key: 'dayGridDay', label: '日' }
]

// ---- AlertCalendarPage — main page component ----
export default function AlertCalendarPage() {
  const calendarRef = useRef<FullCalendar>(null)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const { getBuiildingType } = useDirectory()

  // building_id (required when navigating from ViewerHeader), floor_id (if viewing a specific floor)
  const alertCalendarParams = useMemo(() => {
    const buildingId = searchParams.get('building_id')
    const floorId = searchParams.get('floor_id')
    return {
      buildingId: buildingId ? Number(buildingId) : undefined,
      floorId: floorId ? Number(floorId) : undefined
    }
  }, [searchParams])

  const now = dayjs()
  const [currentYear, setCurrentYear] = useState(now.year())
  const [currentMonth, setCurrentMonth] = useState(now.month() + 1)
  const [calendarView, setCalendarView] =
    useState<CalendarViewType>('dayGridMonth')

  // Toggle between Calendar view and List view
  const [pageViewMode, setPageViewMode] = useState<PageViewMode>('calendar')

  const [clickedDate, setClickedDate] = useState<Date | null>(null)

  const { events, isLoading } = useAlertCalendar({
    year: currentYear,
    month: currentMonth,
    buildingId: alertCalendarParams.buildingId,
    floorId: alertCalendarParams.floorId
  })

  // Open day popup only if the clicked date has at least one alert
  const handleDateClick = useCallback(
    (arg: DateClickArg) => {
      const hasEvents = events.some((ev: AlertCalendarEvent) =>
        dayjs(ev.start).isSame(dayjs(arg.date), 'day')
      )
      if (hasEvents) {
        setClickedDate(arg.date)
      }
    },
    [events]
  )

  const handleMoreLinkClick = useCallback((arg: any) => {
    setClickedDate(arg.date)
    return 'none'
  }, [])

  // Called when FullCalendar changes month/view range → trigger refetch
  const handleDatesSet = useCallback(
    (arg: DatesSetArg) => {
      const mid = new Date((arg.start.getTime() + arg.end.getTime()) / 2)
      const year = mid.getFullYear()
      const month = mid.getMonth() + 1
      if (year !== currentYear || month !== currentMonth) {
        setCurrentYear(year)
        setCurrentMonth(month)
      }
    },
    [currentYear, currentMonth]
  )

  // Calendar navigation helpers
  const handlePrev = () => calendarRef.current?.getApi().prev()
  const handleNext = () => calendarRef.current?.getApi().next()
  const handleToday = () => calendarRef.current?.getApi().today()
  const handleChangeCalendarView = (v: CalendarViewType) => {
    setCalendarView(v)
    calendarRef.current?.getApi().changeView(v)
  }

  const renderEventContent = useCallback(
    (info: EventContentArg) => {
      const folderId = info.event.extendedProps?.folderId as number | undefined
      const childrenId = info.event.extendedProps?.childrenId as
        | number
        | undefined
      const annotationType = info.event.extendedProps?.annotationType as
        | number
        | undefined
      let label = ''

      if (childrenId) {
        label = CommonFunction.getBreadcrumbTextByChidType(
          String(childrenId),
          t,
          getBuiildingType()
        )
      } else if (folderId) {
        label = CommonFunction.getBreadcrumbTextByFolderType(
          String(folderId),
          t
        )
      }

      return (
        <CalendarEventContent
          eventInfo={info}
          label={label}
          annotationType={annotationType as number | undefined}
        />
      )
    },
    [getBuiildingType, t]
  )

  // dayCellContent renderer: returns only the numeric day (no "日" suffix from locale="ja")
  // Wrapped in useCallback so the function reference stays stable across renders,
  // preventing react/no-unstable-nested-components ESLint error
  const renderDayCellContent = useCallback(
    (arg: { date: Date }) => <DayCellNumber date={arg.date} />,
    []
  )

  return (
    <div className="alert-calendar-page">
      {/* ===== PAGE HEADER ===== */}
      <div className="alert-calendar-header">
        <div className="alert-calendar-header-left">
          <button
            type="button"
            className="alert-calendar-back-btn flex items-center gap-1 px-3"
            onClick={() => navigate(-1)}
          >
            <ArrowLeftOutlined />
          </button>

          {pageViewMode === 'calendar' && (
            <div className="alert-calendar-nav mr-2">
              <button
                type="button"
                className="alert-calendar-nav-btn"
                onClick={handlePrev}
                aria-label="前月"
              >
                <ArrowLeftOutlined />
              </button>
              <button
                type="button"
                className="alert-calendar-nav-btn today-btn"
                onClick={handleToday}
              >
                今日
              </button>
              <button
                type="button"
                className="alert-calendar-nav-btn"
                onClick={handleNext}
                aria-label="翌月"
              >
                <RightOutlined />
              </button>
            </div>
          )}

          <h1 className="alert-calendar-title">アラート</h1>
          {pageViewMode === 'calendar' && (
            <span className="alert-calendar-subtitle">
              {currentYear}年 {currentMonth}月
            </span>
          )}
        </div>

        <div className="alert-calendar-header-right">
          {/* Toggle Calendar / List — Feedback: added view switcher */}
          <div className="alert-calendar-view-toggle">
            <button
              type="button"
              className={`alert-calendar-view-btn ${
                pageViewMode === 'calendar' ? 'active' : ''
              }`}
              onClick={() => setPageViewMode('calendar')}
              id="btn-view-calendar"
            >
              <CalendarOutlined className="mr-1" />
              カレンダー
            </button>
            <button
              type="button"
              className={`alert-calendar-view-btn ${
                pageViewMode === 'list' ? 'active' : ''
              }`}
              onClick={() => setPageViewMode('list')}
              id="btn-view-list"
            >
              <UnorderedListOutlined className="mr-1" />
              リスト
            </button>
          </div>

          {/* Calendar nav — only visible in Calendar mode */}
          {pageViewMode === 'calendar' && (
            <div className="alert-calendar-view-toggle">
              {CALENDAR_VIEW_LABELS.map(({ key, label }) => (
                <button
                  type="button"
                  key={key}
                  className={`alert-calendar-view-btn ${
                    calendarView === key ? 'active' : ''
                  }`}
                  onClick={() => handleChangeCalendarView(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ===== CALENDAR VIEW ===== */}
      {pageViewMode === 'calendar' && (
        <>
          {/* Legend */}
          <div className="alert-calendar-legend">
            {events.length > 0 && (
              <Badge
                count={events.length}
                style={{ backgroundColor: '#3163b7', marginLeft: 8 }}
                title={`今月 ${events.length} 件のアラート`}
              />
            )}
          </div>

          <div className="alert-calendar-body">
            {isLoading && (
              <div className="alert-calendar-loading">
                <Spin size="large" />
              </div>
            )}
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              initialDate={dayjs()
                .year(currentYear)
                .month(currentMonth - 1)
                .startOf('month')
                .format('YYYY-MM-DD')}
              locale="ja"
              firstDay={0}
              height="auto"
              events={events}
              eventContent={renderEventContent}
              datesSet={handleDatesSet}
              dateClick={handleDateClick}
              moreLinkClick={handleMoreLinkClick}
              headerToolbar={false}
              dayMaxEvents={3}
              moreLinkContent={(args) => `+${args.num}件`}
              eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }}
              dayCellContent={renderDayCellContent}
            />
          </div>
        </>
      )}

      {/* ===== LIST VIEW ===== */}
      {pageViewMode === 'list' && (
        <AlertListView events={events} isLoading={isLoading} />
      )}

      {/* ===== DAY EVENTS POPUP ===== */}
      <DayEventsPopup
        date={clickedDate}
        events={events}
        onClose={() => setClickedDate(null)}
      />
    </div>
  )
}
